import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import Button from '@/components/ui/Button';
import { NotificationBell } from '@/components/notifications';
import { ContactModal } from '@/components/contact';
import TenantSwitcher from './TenantSwitcher';

interface HeaderProps {
  onMenuToggle?: () => void;
}

export default function Header({ onMenuToggle }: HeaderProps) {
  const { isAuthenticated, userName, logout } = useAuthStore();
  // WP-CONTACT-1: Contact modal state for authenticated users
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left section: hamburger + logo */}
          <div className="flex items-center">
            {/* WP-WEBMOB-1: Hamburger button, visible only below md */}
            {isAuthenticated && onMenuToggle && (
              <button
                type="button"
                onClick={onMenuToggle}
                className="md:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 mr-2"
                aria-label="Open navigation menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}

            {/* Logo */}
            <Link to="/" className="flex items-center">
              <svg
                className="h-8 w-8"
                viewBox="0 0 100 100"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="header-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#0ea5e9' }} />
                    <stop offset="100%" style={{ stopColor: '#8b5cf6' }} />
                  </linearGradient>
                </defs>
                <path
                  d="M20 30 Q50 30 50 50 T80 70"
                  stroke="url(#header-gradient)"
                  strokeWidth="8"
                  fill="none"
                  strokeLinecap="round"
                  opacity="0.8"
                />
                <path
                  d="M20 50 Q50 50 80 50"
                  stroke="url(#header-gradient)"
                  strokeWidth="12"
                  fill="none"
                  strokeLinecap="round"
                />
                <path
                  d="M20 70 Q50 70 50 50 T80 30"
                  stroke="url(#header-gradient)"
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                  opacity="0.6"
                />
                <circle cx="20" cy="50" r="8" fill="#0ea5e9" />
                <circle cx="80" cy="50" r="8" fill="#8b5cf6" />
              </svg>
              <span className="ml-2 text-xl font-bold text-gray-900">FlowIO</span>
            </Link>
          </div>

          {/* Navigation */}
          {isAuthenticated ? (
            <div className="flex items-center space-x-4">
              <nav className="hidden md:flex space-x-6">
                <Link
                  to="/dashboard"
                  className="text-gray-600 hover:text-gray-900 font-medium"
                >
                  Dashboard
                </Link>
                <Link
                  to="/accounts"
                  className="text-gray-600 hover:text-gray-900 font-medium"
                >
                  Accounts
                </Link>
                <Link
                  to="/flow"
                  className="text-gray-600 hover:text-gray-900 font-medium"
                >
                  Flow
                </Link>
              </nav>

              <div className="flex items-center space-x-2 sm:space-x-3">
                {/* WP-CONTACT-1: Help/Contact button - hidden on mobile (WP-WEBMOB-2) */}
                <button
                  type="button"
                  onClick={() => setContactOpen(true)}
                  className="hidden sm:block text-gray-500 hover:text-gray-700 p-1 rounded-md hover:bg-gray-100 transition-colors"
                  aria-label="Contact support"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                    />
                  </svg>
                </button>
                {/* Notification bell */}
                <NotificationBell />
                {/* Household switcher - only shows when user has 2+ households */}
                <TenantSwitcher />
                {/* Settings + Logout -- hidden on mobile, shown on md+ (WP-WEBMOB-2) */}
                <div className="hidden md:flex items-center space-x-3">
                  <Link to="/settings" className="text-sm text-gray-600 hover:text-gray-900">
                    {userName || 'Settings'}
                  </Link>
                  <Button variant="ghost" size="sm" onClick={() => logout()}>
                    Log out
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              <Link to="/login">
                <Button variant="ghost" size="sm">
                  Sign in
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="primary" size="sm">
                  Get started
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* WP-CONTACT-1: Contact modal for authenticated users */}
      <ContactModal
        isOpen={contactOpen}
        onClose={() => setContactOpen(false)}
        topic="support"
      />
    </header>
  );
}
